import React from 'react';
import { useTheme } from '@mui/material/styles';
// interface
import { Badge, Company } from '../../interfaces/users.interface';
import { Survey } from '../../interfaces/survey.interface';
import { AssessmentSurvey, AssessorAnswer } from '../../interfaces/assessment.interface';
// services
import { LinearProgressPercent, MILESTONE_STATUS, assessmentDisplayLogic, getRemediationCounts, surveyDisplayLogic } from '../../services/surveyHelpers';
// components
import { Workflow, WorkflowDisplayStatus } from '../../interfaces/workflow.interface';
import { Box, Typography } from '@mui/material';
import { useWorkflowMap } from '../../hooks/useWorkflowMap';

type WorkflowStatusProps = {
  workflowDisplayStatusObject?: WorkflowDisplayStatus
  hasGoldExpired?: boolean;
  hasBlueExpired?: boolean;
  hideProgressBar?: boolean;
  hideRemediationCounts?: boolean;
  workflowDisplayType?: string;
};

export function getWorkflowStatusForStep({
  workflow,
  company,
  survey,
  assessmentSurvey,
}: {
  workflow?: Workflow;
  company: Company;
  survey?: Survey;
  assessmentSurvey?: AssessmentSurvey;
}): string {
  const surveyStatusDisplay = surveyDisplayLogic({
    survey,
    companyCerts: company.companyCertifications,
    workflow,
  });

  // If JSX.Element(ie. Progress Bar) return default in-progress status
  const surveyDisplayLogicString = typeof surveyStatusDisplay === 'string' ? surveyStatusDisplay : MILESTONE_STATUS.QUESTIONNAIRE_IN_PROGRESS;

  const remediationDisplayStatus = () => {
    if (!assessmentSurvey) return '';
    const status = (assessmentSurvey.assessorAnswers as AssessorAnswer[]).some(it => ['remediation'].indexOf(it?.status || '') > -1 && !it.vendorStatus)
      ? MILESTONE_STATUS.ASSESSMENT_PENDING_REMEDIATION_PLANS
      : MILESTONE_STATUS.REMEDIATION_PLANS_SUBMITTED;
    return status;
  };

  const hasRemediationCounts = Boolean(workflow?.status === 'Remediation' || (workflow?.status === 'Complete' && assessmentSurvey?.bestPracticeRemediationCount));

  return !workflow?.assessmentSurvey
    ? surveyDisplayLogicString
    : hasRemediationCounts
      ? remediationDisplayStatus()
      : assessmentDisplayLogic(workflow.status, (workflow.badges ?? []) as Badge[]);
}

const WorkflowStatus = ({
  workflowDisplayStatusObject,
  hasGoldExpired = false,
  hasBlueExpired = false,
  hideProgressBar = false,
  hideRemediationCounts = false,
  workflowDisplayType,
}: WorkflowStatusProps) => {
  const theme = useTheme();
  const workflowMap = useWorkflowMap();

  if (!workflowDisplayStatusObject) return null;

  const {
    answersCount,
    questionsCount,
    remediationCounts,
    showRemediationCounts,
    status,
  } = workflowDisplayStatusObject;

  const showLinearProgress = !hideProgressBar && questionsCount > 0 && answersCount >= 0;

  const { bestPracticesQuestionsWithoutRemediationPlan, implementationGuidanceQuestionsWithoutRemediationPlan } = remediationCounts || {};

  const showRemediationCountsUi = !hideRemediationCounts && showRemediationCounts && remediationCounts && (!!bestPracticesQuestionsWithoutRemediationPlan || !!implementationGuidanceQuestionsWithoutRemediationPlan );

  return (
    <Box sx={{ minWidth: 150 }}>
      { workflowDisplayType && (
      <Typography
      sx={{
        color: theme.palette.customThemeColors?.grey,
        lineHeight: 1.25,
        mb: 0.25,
      }}
    >
      {workflowMap[workflowDisplayType]?.version || 'Unknown Version'}
    </Typography>
      )}

      <Typography
        sx={{
          color: theme.palette.customThemeColors?.darkGrey,
          fontWeight: 400,
          lineHeight: 1.25,
        }}
      >
        {status}
      </Typography>

      {showLinearProgress && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ width: 122 }}>
            <LinearProgressPercent
              questionsCount={questionsCount}
              answersCount={answersCount}
            />
          </Box>
        </Box>
      )}

      {showRemediationCountsUi && getRemediationCounts({ remediationCounts })}

      {hasGoldExpired && (
        <Typography
          sx={{
            color: theme.palette.customThemeColors?.darkGrey,
            fontWeight: 400,
            display: 'block',
          }}
        >
          Assessment Expired
        </Typography>
      )}

      {!hasGoldExpired && hasBlueExpired && (
        <Typography
          sx={{
            color: theme.palette.customThemeColors?.darkGrey,
            fontWeight: 400,
            display: 'block',
          }}
        >
          Questionnaire Expired
        </Typography>
      )}
    </Box>
  );
};

export default WorkflowStatus;
